import { RESUME_SCHEMA_DESCRIPTION } from '../utils/resumeSchema'

const GEMINI_API_BASE_URL = 'https://generativelanguage.googleapis.com/v1'
// List of models to try in order of preference
// gemini-2.0-flash is the latest and most capable
const GEMINI_MODELS = ['gemini-2.5-flash-lite']

/**
 * Calls Gemini API to parse resume text and map it to the resume schema
 * @param pdfText - The extracted text from the PDF
 * @returns Promise that resolves to the parsed resume JSON object
 */
export async function parseResumeWithGemini(pdfText: string): Promise<any> {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY

  if (!apiKey) {
    throw new Error(
      'Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY in your environment variables.'
    )
  }

  // Create the prompt for Gemini
  const prompt = `You are an expert at parsing resume/CV documents and extracting structured information.

${RESUME_SCHEMA_DESCRIPTION}

Extract all relevant information from the following resume text and map it to the resume schema structure above. 

CRITICAL: You must return ONLY a valid JSON object that matches the schema exactly. Do NOT include any markdown formatting, code blocks, backticks, or explanatory text. Return ONLY the raw JSON object starting with { and ending with }.

Resume Text:
${pdfText}

Return the complete resume JSON object matching the schema (JSON only, no markdown):`

  // Try each model until one works
  let lastError: Error | null = null

  for (const model of GEMINI_MODELS) {
    try {
      const response = await fetch(
        `${GEMINI_API_BASE_URL}/models/${model}:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: prompt
                  }
                ]
              }
            ],
            generationConfig: {
              temperature: 0.1, // Low temperature for more consistent, structured output
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 18192
              // Note: responseMimeType is not supported by REST API, we parse JSON manually
            }
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage =
          errorData.error?.message || `HTTP error! status: ${response.status}`

        // If model not found, try next model
        if (
          errorMessage.includes('not found') ||
          errorMessage.includes('not supported')
        ) {
          console.warn(`Model ${model} not available, trying next model...`)
          lastError = new Error(`Model ${model} is not available`)
          continue // Try next model
        }

        // Handle specific error cases that shouldn't retry
        if (response.status === 401 || response.status === 403) {
          throw new Error(
            `Authentication failed: ${errorMessage}. Please check your Gemini API key.`
          )
        }
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please wait a moment and try again.')
        }
        if (response.status >= 500) {
          throw new Error(
            `Gemini API server error: ${errorMessage}. Please try again later.`
          )
        }

        // For 400 errors, try next model
        if (response.status === 400) {
          console.warn(`Model ${model} returned 400 error, trying next model...`)
          lastError = new Error(`Invalid request for model ${model}: ${errorMessage}`)
          continue
        }

        throw new Error(`Gemini API error: ${errorMessage}`)
      }

      const data = await response.json()

      // Extract the generated content
      if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
        throw new Error('Invalid response format from Gemini API')
      }

      const content = data.candidates[0].content.parts[0].text

      if (!content) {
        throw new Error('No content generated by Gemini API')
      }

      // Parse the JSON response
      try {
        // Remove any markdown code blocks if present
        const cleanedContent = content
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .trim()
        const parsedResume = JSON.parse(cleanedContent)
        return parsedResume
      } catch (parseError) {
        console.error('Failed to parse Gemini response:', content)
        throw new Error(
          `Failed to parse response from Gemini API. The response may not be valid JSON. Error: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`
        )
      }
    } catch (error) {
      // If it's a model-specific error, try next model
      if (error instanceof Error && error.message.includes('not found')) {
        lastError = error
        continue
      }

      // Re-throw authentication and rate limit errors immediately
      if (error instanceof Error) {
        if (
          error.message.includes('API key') ||
          error.message.includes('Authentication') ||
          error.message.includes('Rate limit')
        ) {
          throw error
        }

        // Handle network errors
        if (
          error.message.includes('fetch') ||
          error.message.includes('network') ||
          error.message.includes('Failed to fetch')
        ) {
          throw new Error(
            'Network error: Unable to connect to Gemini API. Please check your internet connection and try again.'
          )
        }
      }

      // For other errors, try next model
      lastError = error instanceof Error ? error : new Error('Unknown error')
      continue
    }
  }

  // If we've tried all models and none worked, throw the last error
  throw (
    lastError ||
    new Error('All Gemini models failed. Please check your API key and try again.')
  )
}
